import type { ContractReconciliationDecision } from "@closer/shared";
import { Button, FormField, Input, Modal, Select, useToast } from "@closer/ui";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api } from "../lib/apiClient";
import { useAuth } from "../lib/authContext";

/**
 * MUTABAKAT KARARI — SAHİP ve KARŞI TARAF için tek diyalog (CLOS-162 · CLOS-883).
 *
 * ⚠ Gerekçe zorunluluğu burada TEK YERDE: olumsuz kararda not istenmezse sunucu zaten
 * reddediyor, ama kullanıcıyı duvara çarptırmak yerine düğme kapalı tutuluyor. Kural iki
 * kopyada yaşasaydı biri gün gelip sunucudan ayrışırdı.
 */

export type ReconciliationDecisionTarget = { reconciliationId: string; taskId: string };

export function ReconciliationDecisionDialog({
  open,
  kararYolu,
  hedef,
  onClose,
  onDone,
}: {
  open: boolean;
  /**
   * ⚠ YOL DIŞARIDAN: aynı karar İKİ YÜZEYDE veriliyor — sahip tarafı `/api/contracts`,
   * karşı taraf `/api/shared-contracts` (CLOS-883). Diyaloğu kopyalasaydık gerekçe
   * zorunluluğu, karar seçenekleri ve "geri alınamaz" uyarısı iki yerde yaşar ve bir gün
   * ayrışırdı; bu depoda `WorkflowCancelDialog` ve `UyeListesiDurumu` aynı gerekçeyle
   * ortaklaştırıldı. Değişen tek şey ucun adresi, verilen karar aynı.
   */
  kararYolu: (hedef: ReconciliationDecisionTarget) => string;
  hedef: ReconciliationDecisionTarget | null;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();

  const [decision, setDecision] = useState<ContractReconciliationDecision>("agreed");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    setDecision("agreed");
    setNote("");
  }, [open]);

  const karar = useMutation({
    mutationFn: async () => {
      if (!hedef) throw new Error("Görev seçilmedi");
      return api(kararYolu(hedef), {
        method: "POST",
        tenantId,
        body: JSON.stringify({ decision, ...(note.trim() ? { note: note.trim() } : {}) }),
      });
    },
    onSuccess: async () => {
      toast.success("Mutabakat kararı kaydedildi");
      await onDone();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Karar kaydedilemedi"),
  });

  const gerekceZorunlu = decision !== "agreed";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Mutabakat kararı"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            onClick={() => karar.mutate()}
            loading={karar.isPending}
            disabled={gerekceZorunlu && note.trim() === ""}
          >
            Kararı kaydet
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-xs text-fg-soft">
          Karar temsil ettiğiniz taraf adına verilir ve geri alınamaz.
        </p>
        <FormField label="Karar">
          <Select
            id="mutabakat-karar"
            value={decision}
            onChange={(olay) => setDecision(olay.target.value as ContractReconciliationDecision)}
          >
            <option value="agreed">Mutabıkız</option>
            <option value="disagreed">Mutabık değiliz</option>
            <option value="changes_requested">Değişiklik istiyoruz</option>
          </Select>
        </FormField>
        <FormField label={gerekceZorunlu ? "Gerekçe (zorunlu)" : "Not (opsiyonel)"}>
          <Input
            id="mutabakat-not"
            value={note}
            onChange={(olay) => setNote(olay.target.value)}
            placeholder="Örn. Teslim süresi 30 güne çekilmeli"
          />
        </FormField>
      </div>
    </Modal>
  );
}
